import clsx from 'clsx'
import type { ReactNode } from 'react'

export interface SetupStep {
  /** Short label shown in the progress header ("Connect", "Verify", ...). */
  title: string
  /** Rich body for the step — description, config snippets, actions. */
  children: ReactNode
}

interface SetupStepperProps {
  /** 0-based index of the currently visible step. */
  activeStep: number
  /** Called when the user jumps via the step pills / back / continue. */
  onStepChange: (index: number) => void
  steps: SetupStep[]
  className?: string
}

/**
 * Shared guided-flow primitive for every Setup Instructions page.
 *
 * UX contract (per ui-ux-pro-max "stepper/onboarding" guidance):
 * - Always shows "Step N of M" progress — never a bare numbered list.
 * - Steps are freely navigable (non-linear): users can go back/skip ahead.
 *   No forced linear tour.
 * - Content is keyboard-operable: pills are buttons, focus ring is visible,
 *   and the active panel announces via aria-live (respects reduced-motion
 *   through `motion-safe:` transitions).
 */
export default function SetupStepper({
  activeStep,
  onStepChange,
  steps,
  className,
}: SetupStepperProps) {
  const total = steps.length
  const clamped = Math.min(Math.max(activeStep, 0), total - 1)
  const current = steps[clamped]

  return (
    <section aria-label="Setup steps" className={className}>
      {/* Progress header */}
      <div className="flex flex-wrap items-center gap-2">
        <p aria-live="polite" className="text-sm text-harbor-text-secondary">
          Step {clamped + 1} of {total}
        </p>
        <div
          className="h-1.5 flex-1 min-w-24 overflow-hidden rounded-full bg-harbor-surface-raised"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={clamped + 1}
          aria-label={`Step ${clamped + 1} of ${total}: ${current.title}`}
        >
          <div
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 motion-safe:transition-[width] motion-reduce:transition-none"
            style={{ width: `${((clamped + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      {/* Step pills */}
      <ol className="mt-4 flex flex-wrap gap-2" aria-label="Choose a step">
        {steps.map((step, index) => {
          const isActive = index === clamped
          const isDone = index < clamped
          return (
            <li key={step.title}>
              <button
                type="button"
                onClick={() => onStepChange(index)}
                aria-current={isActive ? 'step' : undefined}
                className={clsx(
                  'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-harbor-ring-offset',
                  isActive
                    ? 'border-emerald-500 bg-emerald-600/20 text-emerald-300'
                    : 'border-harbor-border bg-harbor-surface text-harbor-text-secondary hover:border-emerald-700 hover:text-harbor-text'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold',
                    isActive
                      ? 'bg-emerald-500 text-white'
                      : isDone
                        ? 'bg-emerald-600/40 text-emerald-200'
                        : 'bg-harbor-surface-raised text-harbor-text-muted'
                  )}
                >
                  {isDone ? '✓' : index + 1}
                </span>
                {step.title}
              </button>
            </li>
          )
        })}
      </ol>

      {/* Active step panel */}
      <div
        key={clamped}
        className="mt-4 rounded-xl border border-harbor-border bg-harbor-surface p-6"
      >
        <h2 className="text-base font-semibold text-harbor-text">
          <span className="text-harbor-text-muted">Step {clamped + 1}:</span> {current.title}
        </h2>
        <div className="mt-3 text-sm text-harbor-text-secondary">{current.children}</div>
      </div>

      {/* Back / Continue */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => onStepChange(clamped - 1)}
          disabled={clamped === 0}
          className="rounded-lg border border-harbor-border bg-harbor-surface px-4 py-2 text-sm font-medium text-harbor-text-secondary transition-colors hover:text-harbor-text disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-harbor-ring-offset"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => onStepChange(clamped + 1)}
          disabled={clamped >= total - 1}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-harbor-ring-offset"
        >
          Continue
        </button>
      </div>
    </section>
  )
}
